import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useNotification } from '@/components/NotificationProvider';
import { usePermissions } from '@/components/PermissionsProvider';
import { 
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Search, Maximize2, Minimize2,
  Wrench, AlertTriangle, Plus, X, Clock, Scissors
} from 'lucide-react';
import { formatDisplayDate, formatDateForInput } from '@/utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function EquipmentGanttView({ open, onOpenChange }) {
  const notification = useNotification();
  const canEdit = usePermissions().canEdit('equipment_planner');
  const containerRef = useRef(null);
  const ganttAreaRef = useRef(null);
  const navigateTimeRef = useRef(null);
  const wheelDetachRef = useRef(null);
  // Ref callback: attaches the Shift+scroll wheel listener exactly when the Gantt
  // content node mounts (the Radix Sheet portal mounts content after effects run,
  // so a plain useEffect on a ref would see null — a ref callback is reliable).
  const setGanttAreaNode = useCallback((node) => {
    ganttAreaRef.current = node;
    if (wheelDetachRef.current) { wheelDetachRef.current(); wheelDetachRef.current = null; }
    if (node) {
      const el = node.closest('[data-radix-scroll-area-viewport]') || node;
      const onWheel = (e) => { if (e.shiftKey) { e.preventDefault(); navigateTimeRef.current && navigateTimeRef.current(e.deltaY > 0 ? 1 : -1); } };
      el.addEventListener('wheel', onWheel, { passive: false });
      wheelDetachRef.current = () => el.removeEventListener('wheel', onWheel);
    }
  }, []);
  const [equipment, setEquipment] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [owners, setOwners] = useState([]);
  
  const [timeScale, setTimeScale] = useState('weekly');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all'); // specific project ID filter
  const [onlyAllocated, setOnlyAllocated] = useState(false); // show only equipment with active allocations

  // Slide state
  const [sliding, setSliding] = useState(null);
  const [didSlide, setDidSlide] = useState(false);
  // Resize state
  const [resizing, setResizing] = useState(null);
  const [didResize, setDidResize] = useState(false);

  // Collapsed categories
  const [collapsedCategories, setCollapsedCategories] = useState({});

  // Multi-select for batch allocation
  const [selectedIds, setSelectedIds] = useState([]);

  // Add allocation modal (single or batch)
  const [allocModal, setAllocModal] = useState(null); // { equipmentId | equipmentIds, equipmentName | count, projectId, startDate, endDate, isOverride }

  // Conflicts (double-bookings)
  const [conflicts, setConflicts] = useState([]);
  const [showConflictsModal, setShowConflictsModal] = useState(false);

  // Split allocation modal
  const [splitModal, setSplitModal] = useState(null); // { allocationId, equipmentItem, project, splitDate }

  useEffect(() => {
    if (open) fetchData();
  }, [open]);

  const fetchData = async () => {
    try {
      const [eqRes, allocRes, projRes, catRes, ownerRes, conflictsRes] = await Promise.all([
        axios.get(`${API}/equipment`, { headers: getAuthHeader() }),
        axios.get(`${API}/allocations/equipment`, { headers: getAuthHeader() }),
        axios.get(`${API}/projects`, { headers: getAuthHeader() }),
        axios.get(`${API}/categories?type=equipment_category`, { headers: getAuthHeader() }),
        axios.get(`${API}/categories?type=ownership_entity`, { headers: getAuthHeader() }),
        axios.get(`${API}/allocations/conflicts`, { headers: getAuthHeader() }).catch(() => ({ data: { equipment_conflicts: [] } })),
      ]);
      setEquipment(eqRes.data);
      setAllocations(allocRes.data);
      setProjects(projRes.data);
      setCategories(catRes.data.map(c => c.name));
      setOwners(ownerRes.data.map(c => c.name));
      setConflicts(conflictsRes.data?.equipment_conflicts || []);
    } catch {
      notification.error('Failed to load equipment data');
    }
  };

  // Timeline generation (matches Personnel)
  const generateTimeline = () => {
    const timeline = [];
    const start = new Date(currentDate);
    
    if (timeScale === 'daily') {
      start.setDate(start.getDate() - start.getDay());
      for (let i = 0; i < 21; i++) {
        const date = new Date(start);
        date.setDate(start.getDate() + i);
        timeline.push({ 
          date, 
          label: date.getDate().toString(), 
          subLabel: date.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', year: '2-digit' }),
          isWeekend: date.getDay() === 0 || date.getDay() === 6
        });
      }
    } else if (timeScale === 'weekly') {
      start.setDate(1);
      for (let i = 0; i < 13; i++) {
        const weekStart = new Date(start);
        weekStart.setDate(start.getDate() + (i * 7));
        timeline.push({ 
          date: weekStart, 
          label: `W${Math.ceil(weekStart.getDate() / 7)}`,
          subLabel: weekStart.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })
        });
      }
    } else {
      start.setDate(1);
      for (let i = 0; i < 3; i++) {
        const month = new Date(start);
        month.setMonth(start.getMonth() + i);
        timeline.push({ 
          date: month, 
          label: month.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' }),
          subLabel: month.getFullYear().toString()
        });
      }
    }
    return timeline;
  };

  const timeline = generateTimeline();
  const timelineStart = timeline[0]?.date || new Date();
  const timelineEnd = timeline[timeline.length - 1]?.date || new Date();
  
  const getAdjustedTimelineEnd = () => {
    const adjustedEnd = new Date(timelineEnd);
    if (timeScale === 'daily') adjustedEnd.setDate(adjustedEnd.getDate() + 1);
    else if (timeScale === 'weekly') adjustedEnd.setDate(adjustedEnd.getDate() + 7);
    else adjustedEnd.setMonth(adjustedEnd.getMonth() + 1);
    return adjustedEnd;
  };

  const getBarStyle = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const adjustedEnd = getAdjustedTimelineEnd();
    
    const totalDays = (adjustedEnd - timelineStart) / (1000 * 60 * 60 * 24);
    const barStart = Math.max(0, (start - timelineStart) / (1000 * 60 * 60 * 24));
    const barEnd = Math.min(totalDays, (end - timelineStart) / (1000 * 60 * 60 * 24));
    
    if (barEnd < 0 || barStart > totalDays) return null;
    
    const left = (barStart / totalDays) * 100;
    const width = ((barEnd - barStart) / totalDays) * 100;
    
    return { left: `${Math.max(0, left)}%`, width: `${Math.min(100 - left, Math.max(1, width))}%` };
  };

  const getMarkerPosition = (date) => {
    const markerDate = new Date(date);
    const adjustedEnd = getAdjustedTimelineEnd();
    if (markerDate < timelineStart || markerDate > adjustedEnd) return null;
    const totalDays = (adjustedEnd - timelineStart) / (1000 * 60 * 60 * 24);
    const position = (markerDate - timelineStart) / (1000 * 60 * 60 * 24);
    return `${(position / totalDays) * 100}%`;
  };

  const getProjectById = (id) => projects.find(p => p.id === id);
  
  const getEquipmentAllocations = (equipmentId) => {
    return allocations.filter(a => a.equipment_id === equipmentId && a.status === 'active');
  };

  // Assign each allocation to a "lane" so overlapping bars stack vertically instead of overlap
  const assignLanes = (allocs) => {
    const sorted = [...allocs].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    const lanes = []; // index -> end date of the most-recent allocation on that lane
    return sorted.map(a => {
      const start = new Date(a.start_date);
      const end = new Date(a.end_date);
      let laneIdx = lanes.findIndex(le => le <= start);
      if (laneIdx === -1) {
        laneIdx = lanes.length;
        lanes.push(end);
      } else {
        lanes[laneIdx] = end;
      }
      return { ...a, _lane: laneIdx };
    });
  };

  // Number of days in an allocation (inclusive)
  const allocDurationDays = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
    return Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1);
  };

  // Color by project status (matches planner)
  const getProjectColor = (status) => {
    if (status === 'Secured') return 'bg-emerald-500';
    if (status === 'Likely') return 'bg-amber-500';
    if (status === 'Prospect') return 'bg-blue-500';
    return 'bg-slate-500';
  };

  const getProjectBarStyle = (project) => {
    // Use label_color if set
    if (project?.label_color) {
      if (project.status === 'Secured') return { background: project.label_color };
      // Striped with label color for forecasted
      return { background: `repeating-linear-gradient(135deg, ${project.label_color}, ${project.label_color} 4px, ${project.label_color}80 4px, ${project.label_color}80 8px)` };
    }
    if (project?.status === 'Secured') return {};
    const color = project?.status === 'Likely' ? 'rgba(245, 158, 11, 0.85)' : 'rgba(59, 130, 246, 0.85)';
    const stripe = project?.status === 'Likely' ? 'rgba(245, 158, 11, 0.5)' : 'rgba(59, 130, 246, 0.5)';
    return { background: `repeating-linear-gradient(135deg, ${color}, ${color} 4px, ${stripe} 4px, ${stripe} 8px)` };
  };

  const getProjectColorLabel = (status) => {
    if (status === 'Secured') return 'Secured';
    return 'Forecasted';
  };

  // Navigation
  const navigateTime = (direction) => {
    const newDate = new Date(currentDate);
    if (timeScale === 'daily') newDate.setDate(newDate.getDate() + (direction * 7));
    else if (timeScale === 'weekly') newDate.setMonth(newDate.getMonth() + direction);
    else newDate.setMonth(newDate.getMonth() + (direction * 3));
    setCurrentDate(newDate);
  };
  // Keep the wheel handler's navigateTime reference current without re-attaching the listener.
  navigateTimeRef.current = navigateTime;

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  useEffect(() => {
    const handleFullScreenChange = () => setIsFullScreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  // Slide handlers (drag middle of bar)
  const handleSlideStart = (e, allocationId) => {
    if (!canEdit) return;
    if (resizing) return;
    e.stopPropagation(); e.preventDefault();
    const alloc = allocations.find(a => a.id === allocationId);
    if (!alloc) return;
    setDidSlide(false);
    setSliding({ allocationId, initialX: e.clientX, initialStart: new Date(alloc.start_date), initialEnd: new Date(alloc.end_date) });
  };
  const handleSlideMove = (e) => {
    if (!sliding || !ganttAreaRef.current) return;
    const rect = ganttAreaRef.current.getBoundingClientRect();
    const adjustedEnd = getAdjustedTimelineEnd();
    const totalDays = (adjustedEnd - timelineStart) / 86400000;
    const daysPerPixel = totalDays / rect.width;
    const daysMoved = Math.round((e.clientX - sliding.initialX) * daysPerPixel);
    if (daysMoved === 0) return;
    setDidSlide(true);
    setAllocations(prev => prev.map(a => {
      if (a.id !== sliding.allocationId) return a;
      const ns = new Date(sliding.initialStart); ns.setDate(ns.getDate() + daysMoved);
      const ne = new Date(sliding.initialEnd); ne.setDate(ne.getDate() + daysMoved);
      return { ...a, start_date: ns.toISOString(), end_date: ne.toISOString() };
    }));
  };
  const handleSlideEnd = async () => {
    if (!sliding) return;
    const alloc = allocations.find(a => a.id === sliding.allocationId);
    if (alloc && didSlide) {
      try {
        await axios.put(`${API}/allocations/equipment/${alloc.id}`, {
          equipment_id: alloc.equipment_id, project_id: alloc.project_id,
          start_date: alloc.start_date, end_date: alloc.end_date, status: alloc.status, version: alloc.version || null
        }, { headers: getAuthHeader() });
        notification.success('Allocation moved');
        fetchData();
      } catch { notification.error('Failed to move'); fetchData(); }
    }
    setSliding(null); setTimeout(() => setDidSlide(false), 100);
  };

  // Resize handlers (drag edges)
  const handleResizeStart = (e, allocationId, edge, currentDate) => {
    if (!canEdit) return;
    if (sliding) return;
    e.stopPropagation(); e.preventDefault();
    setDidResize(false);
    setResizing({ allocationId, edge, initialX: e.clientX, initialDate: new Date(currentDate) });
  };
  const handleResizeMove = (e) => {
    if (!resizing || !ganttAreaRef.current) return;
    const rect = ganttAreaRef.current.getBoundingClientRect();
    const adjustedEnd = getAdjustedTimelineEnd();
    const totalDays = (adjustedEnd - timelineStart) / 86400000;
    const daysPerPixel = totalDays / rect.width;
    const daysMoved = Math.round((e.clientX - resizing.initialX) * daysPerPixel);
    if (daysMoved === 0) return;
    setDidResize(true);
    setAllocations(prev => prev.map(a => {
      if (a.id !== resizing.allocationId) return a;
      const newDate = new Date(resizing.initialDate); newDate.setDate(newDate.getDate() + daysMoved);
      if (resizing.edge === 'start') return { ...a, start_date: newDate.toISOString() };
      return { ...a, end_date: newDate.toISOString() };
    }));
  };
  const handleResizeEnd = async () => {
    if (!resizing) return;
    const alloc = allocations.find(a => a.id === resizing.allocationId);
    if (alloc && didResize) {
      try {
        await axios.put(`${API}/allocations/equipment/${alloc.id}`, {
          equipment_id: alloc.equipment_id, project_id: alloc.project_id,
          start_date: alloc.start_date, end_date: alloc.end_date, status: alloc.status, version: alloc.version || null
        }, { headers: getAuthHeader() });
        notification.success('Allocation resized');
        fetchData();
      } catch { notification.error('Failed to resize'); fetchData(); }
    }
    setResizing(null); setTimeout(() => setDidResize(false), 100);
  };

  // Mouse event listeners
  useEffect(() => {
    if (resizing || sliding) {
      const onMove = (e) => { if (resizing) handleResizeMove(e); if (sliding) handleSlideMove(e); };
      const onUp = () => { if (resizing) handleResizeEnd(); if (sliding) handleSlideEnd(); };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    }
  }, [resizing, sliding, allocations, timelineStart, timeScale]);

  // Shift+scroll for timeline navigation is wired via the setGanttAreaNode ref callback.

  // Days until helper
  const daysUntil = (date) => {
    const today = new Date();
    const target = new Date(date);
    return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
  };

  // Maintenance status badge
  const getMaintenanceBadge = (eq) => {
    if (!eq.next_maintenance_date) return null;
    const days = daysUntil(eq.next_maintenance_date);
    if (days < 0) return { label: 'Overdue', cls: 'bg-red-100 text-red-700 border-red-200' };
    if (days <= 30) return { label: 'Due Soon', cls: 'bg-amber-100 text-amber-700 border-amber-200' };
    return null;
  };

  // Filter equipment
  const filteredEquipment = equipment.filter(eq => {
    // Search filter
    if (searchFilter) {
      const s = searchFilter.toLowerCase();
      const match = (eq.equipment_name || '').toLowerCase().includes(s) ||
        (eq.internal_tag || '').toLowerCase().includes(s) ||
        (eq.serial_number || '').toLowerCase().includes(s) ||
        (eq.category || '').toLowerCase().includes(s) ||
        (eq.ownership || '').toLowerCase().includes(s);
      if (!match) return false;
    }
    // Category filter
    if (categoryFilter !== 'all' && eq.category !== categoryFilter) return false;
    // Owner filter
    if (ownerFilter !== 'all' && eq.ownership !== ownerFilter) return false;
    // Only Allocated filter — must have at least one active allocation matching current status + project filters
    if (onlyAllocated) {
      const allocs = getEquipmentAllocations(eq.id).filter(a => {
        if (projectFilter !== 'all' && a.project_id !== projectFilter) return false;
        if (statusFilter === 'all') return true;
        const project = getProjectById(a.project_id);
        if (!project) return false;
        if (statusFilter === 'secured') return project.status === 'Secured';
        return project.status === 'Likely' || project.status === 'Prospect';
      });
      if (allocs.length === 0) return false;
    }
    // Status filter — show all equipment but filter allocations in rendering
    return true;
  });

  // Group by category
  const groupedEquipment = filteredEquipment.reduce((acc, eq) => {
    const cat = eq.category || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(eq);
    return acc;
  }, {});

  // Get filtered allocations for an equipment based on status filter + specific project filter
  const getFilteredAllocations = (equipmentId) => {
    let allocs = getEquipmentAllocations(equipmentId);
    // Project-specific filter takes precedence
    if (projectFilter !== 'all') {
      allocs = allocs.filter(a => a.project_id === projectFilter);
    }
    if (statusFilter === 'all') return allocs;
    return allocs.filter(a => {
      const project = getProjectById(a.project_id);
      if (!project) return false;
      if (statusFilter === 'secured') return project.status === 'Secured';
      // forecasted = Likely + Prospect
      return project.status === 'Likely' || project.status === 'Prospect';
    });
  };

  // Count active allocations (respecting status filter)
  const totalFilteredAllocations = filteredEquipment.reduce((sum, eq) => sum + getFilteredAllocations(eq.id).length, 0);

  // Unique owner values from current equipment data for filter dropdown
  const uniqueOwners = [...new Set(equipment.map(eq => eq.ownership).filter(Boolean))].sort();

  // Category options for filter in sticky column
  const uniqueCategories = useMemo(() => [...new Set(equipment.map(e => e.category).filter(Boolean))].sort(), [equipment]);

  // Set of allocation IDs that have conflicts (for highlighting bars)
  const conflictingAllocIds = useMemo(() => {
    const s = new Set();
    conflicts.forEach(c => { s.add(c.allocation_1_id); s.add(c.allocation_2_id); });
    return s;
  }, [conflicts]);

  // Collapse/Expand all
  const toggleAllCategories = (collapse) => {
    const state = {};
    Object.keys(groupedEquipment).forEach(cat => { state[cat] = collapse; });
    setCollapsedCategories(state);
  };

  // Quick delete allocation from bar
  const handleDeleteAllocation = (allocId, e) => {
    e.stopPropagation();
    if (!canEdit) return;
    notification.confirm('Remove this allocation?', async () => {
      try {
        await axios.delete(`${API}/allocations/equipment/${allocId}`, { headers: getAuthHeader() });
        notification.success('Allocation removed');
        fetchData();
      } catch (error) {
        notification.error('Failed to remove allocation');
      }
    });
  };

  // Split allocation into two parts at a chosen date
  const handleSplitAllocation = async () => {
    if (!splitModal?.splitDate || !splitModal?.allocationId) return;
    const alloc = allocations.find(a => a.id === splitModal.allocationId);
    if (!alloc) return;
    const splitDate = new Date(splitModal.splitDate);
    const origStart = new Date(alloc.start_date);
    const origEnd = new Date(alloc.end_date);
    if (splitDate <= origStart || splitDate >= origEnd) {
      notification.error('Split date must be between start and end dates');
      return;
    }
    try {
      await axios.delete(`${API}/allocations/equipment/${alloc.id}`, { headers: getAuthHeader() });
      const firstEnd = new Date(splitDate);
      firstEnd.setDate(firstEnd.getDate() - 1);
      await axios.post(`${API}/allocations/equipment`, {
        equipment_id: alloc.equipment_id, project_id: alloc.project_id,
        start_date: origStart.toISOString(), end_date: firstEnd.toISOString(),
        status: alloc.status || 'active',
        is_override: !!alloc.is_override
      }, { headers: getAuthHeader() });
      await axios.post(`${API}/allocations/equipment`, {
        equipment_id: alloc.equipment_id, project_id: alloc.project_id,
        start_date: splitDate.toISOString(), end_date: origEnd.toISOString(),
        status: alloc.status || 'active',
        is_override: !!alloc.is_override
      }, { headers: getAuthHeader() });
      notification.success('Allocation split successfully');
      setSplitModal(null);
      fetchData();
    } catch (error) {
      notification.error(error.response?.data?.detail || 'Failed to split allocation');
    }
  };

  // Handle add allocation (supports single or batch)
  const handleAddAllocation = async (forceOverride = false) => {
    if (!canEdit) return;
    if (!allocModal?.projectId || !allocModal?.startDate || !allocModal?.endDate) {
      notification.error('Project, Start Date and End Date are required');
      return;
    }
    if (new Date(allocModal.endDate) <= new Date(allocModal.startDate)) {
      notification.error('End date must be after start date');
      return;
    }
    const isOverride = !!forceOverride || !!allocModal.isOverride;
    const ids = allocModal.equipmentIds || [allocModal.equipmentId];
    let created = 0;
    let failed = 0;
    for (const eqId of ids) {
      try {
        await axios.post(`${API}/allocations/equipment`, {
          equipment_id: eqId,
          project_id: allocModal.projectId,
          start_date: new Date(allocModal.startDate).toISOString(),
          end_date: new Date(allocModal.endDate).toISOString(),
          status: 'active',
          is_override: isOverride,
          version: null
        }, { headers: getAuthHeader() });
        created++;
      } catch (error) {
        failed++;
        console.warn('Allocation skip', eqId, error.response?.data?.detail);
      }
    }
    if (created > 0) {
      notification.success(`${created} allocation${created > 1 ? 's' : ''} created${isOverride ? ' (Override)' : ''}${failed > 0 ? ` • ${failed} skipped` : ''}`);
    } else {
      notification.error('Failed to create allocations');
    }
    setAllocModal(null);
    setSelectedIds([]);
    fetchData();
  };

  // Detect category mismatch when project is selected in the modal
  const allocCategoryMismatch = useMemo(() => {
    if (!allocModal?.projectId) return null;
    const project = projects.find(p => p.id === allocModal.projectId);
    if (!project) return null;
    const ids = allocModal.equipmentIds || [allocModal.equipmentId];
    const forecasts = project.equipment_forecasts || [];
    if (forecasts.length === 0) {
      return `No equipment requirements defined for "${project.name}".`;
    }
    const mismatched = ids.filter(id => {
      const eq = equipment.find(e => e.id === id);
      if (!eq) return false;
      return !forecasts.find(f => f.category === eq.category && (!f.subcategory || f.subcategory === eq.subcategory));
    });
    if (mismatched.length === 0) return null;
    if (ids.length === 1) {
      const eq = equipment.find(e => e.id === mismatched[0]);
      return `"${project.name}" requires: ${forecasts.map(f => f.subcategory ? `${f.category}/${f.subcategory}` : f.category).join(', ')}. This equipment is ${eq?.category}${eq?.subcategory ? '/' + eq.subcategory : ''}.`;
    }
    return `${mismatched.length} of ${ids.length} selected equipment do not match the project's forecast categories (${forecasts.map(f => f.category).join(', ')}).`;
  }, [allocModal?.projectId, allocModal?.equipmentId, allocModal?.equipmentIds, projects, equipment]);

  // Detect dates that fall outside the project's start/end window
  const allocDateBoundsMismatch = useMemo(() => {
    if (!allocModal?.projectId || !allocModal?.startDate || !allocModal?.endDate) return null;
    const project = projects.find(p => p.id === allocModal.projectId);
    if (!project) return null;
    const projStart = new Date(project.start_date);
    const projEnd = new Date(project.end_date);
    const allocStart = new Date(allocModal.startDate);
    const allocEnd = new Date(allocModal.endDate);
    // Strip time components
    projStart.setHours(0, 0, 0, 0);
    projEnd.setHours(0, 0, 0, 0);
    allocStart.setHours(0, 0, 0, 0);
    allocEnd.setHours(0, 0, 0, 0);
    const startBefore = allocStart < projStart;
    const endAfter = allocEnd > projEnd;
    if (!startBefore && !endAfter) return null;
    const parts = [];
    const fmt = (d) => d.toLocaleDateString('en-AU');
    if (startBefore) parts.push(`Start ${fmt(allocStart)} is before project start ${fmt(projStart)}`);
    if (endAfter) parts.push(`End ${fmt(allocEnd)} is after project end ${fmt(projEnd)}`);
    return `${parts.join(' • ')}. Project "${project.name}" runs ${fmt(projStart)} → ${fmt(projEnd)}.`;
  }, [allocModal?.projectId, allocModal?.startDate, allocModal?.endDate, projects]);

  // Combined override warning (category mismatch OR date out-of-bounds)
  const allocOverrideWarning = allocCategoryMismatch || allocDateBoundsMismatch;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-full p-0"
        ref={containerRef}
      >
        <div className={`h-full flex flex-col ${isFullScreen ? 'bg-slate-50' : ''}`}>
          {/* Header */}
          <div className="p-4 border-b border-slate-200 bg-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <SheetTitle className="text-xl font-bold tracking-tight text-slate-900">Equipment Overview</SheetTitle>
                <SheetDescription className="text-sm text-slate-500">
                  View project allocations and maintenance for all equipment
                </SheetDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex border border-slate-300 rounded-md overflow-hidden">
                  <Button variant="ghost" size="sm" onClick={() => setTimeScale('daily')} 
                    className={`rounded-none px-3 h-8 text-xs ${timeScale === 'daily' ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`}
                    data-testid="eq-gantt-day-btn">Day</Button>
                  <Button variant="ghost" size="sm" onClick={() => setTimeScale('weekly')} 
                    className={`rounded-none px-3 h-8 text-xs ${timeScale === 'weekly' ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`}
                    data-testid="eq-gantt-week-btn">Week</Button>
                  <Button variant="ghost" size="sm" onClick={() => setTimeScale('monthly')} 
                    className={`rounded-none px-3 h-8 text-xs ${timeScale === 'monthly' ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`}
                    data-testid="eq-gantt-month-btn">Month</Button>
                </div>
                <Button variant="outline" size="sm" onClick={toggleFullScreen} className="h-8" data-testid="eq-gantt-fullscreen-btn">
                  {isFullScreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Search, Filters, and Navigation */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search equipment..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="pl-8 h-8 text-sm"
                  data-testid="eq-gantt-search"
                />
              </div>

              {/* Status Filter (Secured/Forecasted) */}
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
                data-testid="eq-gantt-status-filter"
              >
                <option value="all">All Statuses</option>
                <option value="secured">Secured Only</option>
                <option value="forecasted">Forecasted Only</option>
              </select>

              {/* Specific Project Filter */}
              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs min-w-[160px] max-w-[240px]"
                data-testid="eq-gantt-project-filter"
                title="Filter by a specific project"
              >
                <option value="all">All Projects</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>

              {/* Category Filter */}
              <select 
                value={categoryFilter} 
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
                data-testid="eq-gantt-category-filter"
              >
                <option value="all">All Categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>

              {/* Owner Filter */}
              <select 
                value={ownerFilter} 
                onChange={(e) => setOwnerFilter(e.target.value)}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
                data-testid="eq-gantt-owner-filter"
              >
                <option value="all">All Owners</option>
                {(owners.length > 0 ? owners : uniqueOwners).map(o => <option key={o} value={o}>{o}</option>)}
              </select>

              {/* Only Allocated toggle */}
              <label className="flex items-center gap-1.5 text-xs text-slate-600 select-none cursor-pointer h-8 px-2 border border-slate-300 rounded-md bg-white hover:bg-slate-50" title="Show only equipment with active allocations">
                <input
                  type="checkbox"
                  checked={onlyAllocated}
                  onChange={(e) => setOnlyAllocated(e.target.checked)}
                  className="rounded border-slate-300"
                  data-testid="eq-gantt-only-allocated"
                />
                <span>Only Allocated</span>
              </label>

              <div className="flex items-center gap-2 ml-auto">
                <Button variant="outline" size="sm" onClick={() => navigateTime(-1)} className="h-8 w-8 p-0" data-testid="eq-gantt-prev-btn">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())} className="h-8 px-3 text-xs" data-testid="eq-gantt-today-btn">
                  Today
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigateTime(1)} className="h-8 w-8 p-0" data-testid="eq-gantt-next-btn">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-4 text-xs">
            <span className="font-semibold text-slate-600">Legend:</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 bg-emerald-500 rounded"></span> Secured</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 rounded" style={{ background: 'repeating-linear-gradient(135deg, rgba(245,158,11,0.85), rgba(245,158,11,0.85) 3px, rgba(245,158,11,0.5) 3px, rgba(245,158,11,0.5) 6px)' }}></span> Likely</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 rounded" style={{ background: 'repeating-linear-gradient(135deg, rgba(59,130,246,0.85), rgba(59,130,246,0.85) 3px, rgba(59,130,246,0.5) 3px, rgba(59,130,246,0.5) 6px)' }}></span> Prospect</span>
            <span className="border-l border-slate-300 pl-4 flex items-center gap-1"><Wrench className="w-3 h-3 text-red-500" /> Maintenance Due</span>
            <span className="border-l border-slate-300 pl-4 text-slate-400">Drag bars to move • Drag edges to resize</span>
          </div>

          {/* Conflict Alert Banner */}
          {conflicts.length > 0 && (
            <div className="mx-4 mt-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2" data-testid="eq-gantt-conflict-banner">
              <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
              <span className="text-sm text-red-700 font-medium">
                {conflicts.length} equipment double-booking{conflicts.length > 1 ? 's' : ''} detected — overlapping project allocations found
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowConflictsModal(true)}
                className="ml-auto h-7 text-xs border-red-300 text-red-700 hover:bg-red-100"
                data-testid="eq-gantt-view-conflicts-btn"
              >
                View Conflicts
              </Button>
            </div>
          )}

          {/* Timeline Header */}
          <div className="flex border-b border-slate-200 bg-white">
            <div className="w-72 flex-shrink-0 px-3 py-2 border-r border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300"
                    data-testid="eq-gantt-select-all"
                    checked={filteredEquipment.length > 0 && selectedIds.length === filteredEquipment.length}
                    ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < filteredEquipment.length; }}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedIds(filteredEquipment.map(eq => eq.id));
                      else setSelectedIds([]);
                    }}
                    title="Select all visible equipment"
                  />
                  <span className="text-xs font-semibold text-slate-600 uppercase">Equipment</span>
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => toggleAllCategories(false)} className="text-[9px] text-slate-500 hover:text-slate-700 underline" data-testid="gantt-expand-all">Expand</button>
                  <span className="text-[9px] text-slate-300">|</span>
                  <button type="button" onClick={() => toggleAllCategories(true)} className="text-[9px] text-slate-500 hover:text-slate-700 underline" data-testid="gantt-collapse-all">Collapse</button>
                </div>
              </div>
            </div>
            <div className="flex-1 flex">
              {timeline.map((t, idx) => (
                <div key={idx} className={`flex-1 text-center border-l border-slate-200 first:border-l-0 py-1 ${t.isWeekend ? 'bg-slate-100' : ''}`}>
                  <div className="text-xs font-medium text-slate-700">{t.label}</div>
                  <div className="text-[10px] text-slate-400">{t.subLabel}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Gantt Content */}
          <ScrollArea className="flex-1">
            <div ref={setGanttAreaNode} className={`min-w-[900px] ${resizing || sliding ? 'select-none' : ''}`}>
              {Object.keys(groupedEquipment).length === 0 ? (
                <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
                  No equipment found matching the current filters
                </div>
              ) : (
                Object.entries(groupedEquipment).map(([category, items]) => (
                  <div key={category}>
                    {/* Category Header - Collapsible */}
                    <div className="flex bg-slate-100 border-b border-slate-200 cursor-pointer" onClick={() => setCollapsedCategories(prev => ({ ...prev, [category]: !prev[category] }))}>
                      <div className="w-72 flex-shrink-0 px-3 py-1.5 border-r border-slate-200 flex items-center gap-1">
                        {collapsedCategories[category] ? <ChevronRight className="w-3 h-3 text-slate-500" /> : <ChevronDown className="w-3 h-3 text-slate-500" />}
                        <span className="text-xs font-semibold text-slate-700">{category}</span>
                        <Badge variant="secondary" className="ml-2 text-[10px]">{items.length}</Badge>
                      </div>
                      <div className="flex-1"></div>
                    </div>

                    {/* Equipment Rows - hidden when collapsed */}
                    {!collapsedCategories[category] && items.map((eq) => {
                      const eqAllocs = getFilteredAllocations(eq.id);
                      const maintBadge = getMaintenanceBadge(eq);
                      const maintMarkerPos = eq.next_maintenance_date ? getMarkerPosition(eq.next_maintenance_date) : null;
                      const displayName = eq.equipment_name || eq.internal_tag || eq.serial_number || 'Unknown';

                      return (
                        <div key={eq.id} className={`flex border-b border-slate-100 hover:bg-slate-50 group ${selectedIds.includes(eq.id) ? 'bg-emerald-50/40' : ''}`}>
                          {/* Name Column */}
                          <div className="w-72 flex-shrink-0 px-3 py-2 border-r border-slate-200">
                            <div className="flex items-center justify-between gap-2">
                              <input
                                type="checkbox"
                                className="rounded border-slate-300 flex-shrink-0"
                                data-testid={`eq-gantt-select-${eq.id}`}
                                checked={selectedIds.includes(eq.id)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedIds(prev => [...prev, eq.id]);
                                  else setSelectedIds(prev => prev.filter(id => id !== eq.id));
                                }}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm font-medium text-slate-800 truncate">{displayName}</span>
                                  {maintBadge && (
                                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-medium ${maintBadge.cls}`}>
                                      {maintBadge.label}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {eq.serial_number && (
                                    <span className="text-[10px] text-slate-400">SN: {eq.serial_number}</span>
                                  )}
                                  {eq.ownership && (
                                    <span className="text-[10px] text-slate-500 font-medium">{eq.ownership}</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {/* Add Allocation button */}
                                {canEdit && (
                                <button
                                  type="button"
                                  onClick={() => setAllocModal({ equipmentId: eq.id, equipmentName: displayName, projectId: '', startDate: formatDateForInput(new Date()), endDate: '' })}
                                  className="h-5 w-5 flex items-center justify-center rounded bg-slate-200 hover:bg-emerald-500 hover:text-white text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Add allocation"
                                  data-testid={`gantt-add-alloc-${eq.id}`}
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                                )}
                                {/* Maintenance warning icon */}
                                {eq.next_maintenance_date && daysUntil(eq.next_maintenance_date) <= 30 && (
                                  <Wrench className={`w-3 h-3 flex-shrink-0 ${daysUntil(eq.next_maintenance_date) < 0 ? 'text-red-500' : 'text-amber-500'}`} 
                                    title={`Maintenance ${daysUntil(eq.next_maintenance_date) < 0 ? 'overdue' : 'due'}: ${formatDisplayDate(eq.next_maintenance_date)}`} />
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Gantt Bar Area */}
                          {(() => {
                            const lanedAllocs = assignLanes(eqAllocs);
                            const numLanes = Math.max(1, ...(lanedAllocs.map(a => a._lane + 1)));
                            const laneHeight = 28; // px per lane
                            const lanePad = 4; // top padding
                            const rowMinHeight = Math.max(40, numLanes * laneHeight + lanePad);
                            return (
                          <div className="flex-1 relative py-1" style={{ minHeight: `${rowMinHeight}px` }}>
                            {/* Timeline grid */}
                            <div className="absolute inset-0 flex">
                              {timeline.map((t, idx) => (
                                <div key={idx} className={`flex-1 border-l border-slate-100 first:border-l-0 ${t.isWeekend ? 'bg-slate-50' : ''}`} />
                              ))}
                            </div>

                            {/* Maintenance marker on timeline */}
                            {maintMarkerPos && (
                              <div
                                className={`absolute top-0.5 w-5 h-5 ${daysUntil(eq.next_maintenance_date) < 0 ? 'bg-red-500' : 'bg-amber-500'} rounded-full flex items-center justify-center z-40 shadow-md border-2 border-white cursor-pointer`}
                                style={{ left: maintMarkerPos }}
                                title={`Maintenance ${daysUntil(eq.next_maintenance_date) < 0 ? 'Overdue' : 'Due'}: ${formatDisplayDate(eq.next_maintenance_date)}`}
                              >
                                <Wrench className="w-3 h-3 text-white" />
                              </div>
                            )}

                            {/* Allocation bars */}
                            {lanedAllocs.length === 0 ? (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-[10px] text-slate-400">No active allocations</span>
                              </div>
                            ) : (
                              lanedAllocs.map((alloc) => {
                                const project = getProjectById(alloc.project_id);
                                const barStyle = getBarStyle(alloc.start_date, alloc.end_date);
                                if (!barStyle) return null;

                                const projectColor = getProjectColor(project?.status);
                                const barInlineStyle = getProjectBarStyle(project);
                                const isSlidingThis = sliding?.allocationId === alloc.id;
                                const isResizingThis = resizing?.allocationId === alloc.id;
                                const isConflict = conflictingAllocIds.has(alloc.id);
                                const durationDays = allocDurationDays(alloc.start_date, alloc.end_date);
                                const topPx = lanePad + (alloc._lane * laneHeight);

                                return (
                                  <div
                                    key={alloc.id}
                                    className="absolute h-7"
                                    style={{ ...barStyle, top: `${topPx}px` }}
                                  >
                                    <div
                                      className={`absolute inset-0 ${projectColor} rounded group ${isSlidingThis || isResizingThis ? 'ring-2 ring-blue-400' : isConflict ? 'ring-2 ring-red-500 ring-offset-1' : 'hover:opacity-90'} ${sliding ? 'cursor-grabbing' : 'cursor-grab'}`}
                                      style={barInlineStyle}
                                      onMouseDown={(e) => {
                                        if (!e.target.closest('[data-resize-handle]')) handleSlideStart(e, alloc.id);
                                      }}
                                      title={`${project?.name || 'Unknown'}: ${formatDisplayDate(alloc.start_date)} - ${formatDisplayDate(alloc.end_date)} • ${durationDays} day${durationDays === 1 ? '' : 's'}\nDrag to slide • Drag edges to resize`}
                                    >
                                      {/* Left resize handle */}
                                      {canEdit && (
                                      <div
                                        data-resize-handle="true"
                                        className="absolute left-0 top-0 w-3 h-full cursor-ew-resize bg-white/40 opacity-0 group-hover:opacity-100 rounded-l transition-opacity z-20 hover:bg-white/60"
                                        onMouseDown={(e) => handleResizeStart(e, alloc.id, 'start', alloc.start_date)}
                                      />
                                      )}
                                      {/* Label */}
                                      <div className="absolute inset-0 flex items-center px-4 pointer-events-none z-10 gap-1.5">
                                        <span className="text-[10px] font-medium truncate drop-shadow-sm text-white">
                                          {project?.name || 'Unknown Project'}
                                        </span>
                                        <span className="text-[9px] text-white/80 font-mono whitespace-nowrap drop-shadow-sm" data-testid={`gantt-bar-duration-${alloc.id}`}>
                                          • {durationDays}d
                                        </span>
                                      </div>
                                      {/* Split button */}
                                      {canEdit && (
                                      <button
                                        data-resize-handle="true"
                                        className="absolute top-0 left-1/2 -translate-x-1/2 h-full w-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20 hover:bg-white/30 rounded"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const mid = new Date((new Date(alloc.start_date).getTime() + new Date(alloc.end_date).getTime()) / 2);
                                          setSplitModal({ allocationId: alloc.id, equipmentItem: eq, project, splitDate: formatDateForInput(mid) });
                                        }}
                                        title="Split allocation"
                                        data-testid={`gantt-split-bar-${alloc.id}`}
                                      >
                                        <Scissors className="w-3 h-3 text-white drop-shadow" />
                                      </button>
                                      )}
                                      {/* Delete button */}
                                      {canEdit && (
                                      <button
                                        data-resize-handle="true"
                                        className="absolute top-0 right-4 h-full w-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20 hover:bg-red-500/50 rounded"
                                        onClick={(e) => handleDeleteAllocation(alloc.id, e)}
                                        title="Remove allocation"
                                      >
                                        <X className="w-3 h-3 text-white drop-shadow" />
                                      </button>
                                      )}
                                      {/* Right resize handle */}
                                      {canEdit && (
                                      <div
                                        data-resize-handle="true"
                                        className="absolute right-0 top-0 w-3 h-full cursor-ew-resize bg-white/40 opacity-0 group-hover:opacity-100 rounded-r transition-opacity z-20 hover:bg-white/60"
                                        onMouseDown={(e) => handleResizeStart(e, alloc.id, 'end', alloc.end_date)}
                                      />
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-xs text-slate-500 flex items-center justify-between" data-testid="eq-gantt-footer">
            <span>{filteredEquipment.length} equipment • {totalFilteredAllocations} active allocations{projectFilter !== 'all' ? ` • filtered to ${projects.find(p => p.id === projectFilter)?.name || 'selected project'}` : ''}</span>
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2" data-testid="eq-gantt-selection-bar">
                <span className="text-xs font-medium text-emerald-700">{selectedIds.length} selected</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedIds([])}
                  className="h-7 text-xs"
                  data-testid="eq-gantt-clear-selection"
                >
                  Clear
                </Button>
                {canEdit && (<>
                <Button
                  size="sm"
                  onClick={() => {
                    // Bulk delete: scope to project filter if active, else all active allocations of selected items
                    const targetAllocs = allocations.filter(a => 
                      a.status === 'active' && 
                      selectedIds.includes(a.equipment_id) &&
                      (projectFilter === 'all' || a.project_id === projectFilter)
                    );
                    if (targetAllocs.length === 0) {
                      notification.error('No active allocations found for the selected equipment' + (projectFilter !== 'all' ? ' in the filtered project' : ''));
                      return;
                    }
                    const projName = projectFilter !== 'all' ? projects.find(p => p.id === projectFilter)?.name : null;
                    notification.confirm(
                      `Delete ${targetAllocs.length} allocation${targetAllocs.length > 1 ? 's' : ''} across ${selectedIds.length} equipment${projName ? ` from "${projName}"` : ''}?\n\nThis cannot be undone.`,
                      async () => {
                        let deleted = 0;
                        for (const a of targetAllocs) {
                          try {
                            await axios.delete(`${API}/allocations/equipment/${a.id}`, { headers: getAuthHeader() });
                            deleted++;
                          } catch { /* skip */ }
                        }
                        notification.success(`${deleted} allocation${deleted !== 1 ? 's' : ''} removed`);
                        setSelectedIds([]);
                        fetchData();
                      },
                      { title: 'Delete Allocations', confirmLabel: `Delete ${targetAllocs.length}`, confirmClass: 'bg-red-600 hover:bg-red-700' }
                    );
                  }}
                  variant="outline"
                  className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="eq-gantt-delete-selected"
                >
                  Delete Selected
                </Button>
                <Button
                  size="sm"
                  onClick={() => setAllocModal({
                    equipmentIds: [...selectedIds],
                    count: selectedIds.length,
                    projectId: '',
                    startDate: formatDateForInput(new Date()),
                    endDate: ''
                  })}
                  className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="eq-gantt-allocate-selected"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Allocate Selected ({selectedIds.length})
                </Button>
                </>)}
              </div>
            )}
          </div>

          {/* Conflicts Modal - inside SheetContent to stay within Radix focus scope */}
          {showConflictsModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setShowConflictsModal(false)}>
              <div className="bg-white rounded-lg shadow-xl w-[700px] max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="eq-gantt-conflicts-modal">
                <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Equipment Double-Bookings</h3>
                    <p className="text-sm text-slate-500">{conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''} detected</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setShowConflictsModal(false)} className="h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
                </div>
                <div className="flex-1 overflow-y-auto p-5">
                  {conflicts.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No conflicts detected</p>
                  ) : (
                    <div className="space-y-3">
                      {conflicts.map((c, idx) => {
                        const eq = equipment.find(e => e.id === c.resource_id);
                        const proj1 = projects.find(p => p.id === c.allocation_1_project);
                        const proj2 = projects.find(p => p.id === c.allocation_2_project);
                        return (
                          <div key={idx} className="border border-red-200 rounded-lg p-4 bg-red-50/50">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                              <span className="font-semibold text-slate-800">{eq?.equipment_name || eq?.internal_tag || c.resource_id.slice(0, 8)}</span>
                              {eq && <span className="text-xs text-slate-500">{eq.category}{eq.subcategory ? ` / ${eq.subcategory}` : ''}</span>}
                            </div>
                            <div className="grid grid-cols-2 gap-3 ml-6">
                              <div className="bg-white rounded p-2.5 border border-slate-200">
                                <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1">Allocation 1</div>
                                <div className="text-sm font-medium text-slate-800">{proj1?.name || 'Unknown'}</div>
                              </div>
                              <div className="bg-white rounded p-2.5 border border-slate-200">
                                <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1">Allocation 2</div>
                                <div className="text-sm font-medium text-slate-800">{proj2?.name || 'Unknown'}</div>
                              </div>
                            </div>
                            <div className="ml-6 mt-2 flex items-center gap-2 text-xs text-red-700 bg-red-100 rounded px-2.5 py-1.5">
                              <Clock className="w-3.5 h-3.5" />
                              <span>Overlap: <strong>{formatDisplayDate(c.overlap_start)}</strong> — <strong>{formatDisplayDate(c.overlap_end)}</strong></span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Add Allocation Modal - inside SheetContent to stay within Radix focus scope */}
          {allocModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={() => setAllocModal(null)}>
              <div className="bg-white rounded-lg shadow-2xl p-6 w-96 border border-slate-200" onClick={(e) => e.stopPropagation()} data-testid="gantt-alloc-modal">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">{allocModal.equipmentIds ? 'Batch Add Allocation' : 'Add Allocation'}</h3>
                <p className="text-sm text-slate-500 mb-4">
                  {allocModal.equipmentIds
                    ? <>Allocate <span className="font-medium text-slate-700">{allocModal.count} equipment</span> to a project</>
                    : <>Allocate <span className="font-medium text-slate-700">{allocModal.equipmentName}</span> to a project</>}
                </p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">Project *</Label>
                    <select
                      value={allocModal.projectId}
                      onChange={(e) => {
                        const proj = projects.find(p => p.id === e.target.value);
                        setAllocModal({ ...allocModal, projectId: e.target.value, endDate: proj ? formatDateForInput(proj.end_date) : allocModal.endDate });
                      }}
                      className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm mt-1"
                      data-testid="gantt-alloc-project"
                    >
                      <option value="">Select Project</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name} ({p.status})</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">Start Date *</Label>
                    <Input type="date" value={allocModal.startDate} onChange={(e) => setAllocModal({ ...allocModal, startDate: e.target.value })} className="mt-1" data-testid="gantt-alloc-start" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">End Date *</Label>
                    <Input type="date" value={allocModal.endDate} onChange={(e) => setAllocModal({ ...allocModal, endDate: e.target.value })} className="mt-1" data-testid="gantt-alloc-end" />
                  </div>
                  {allocOverrideWarning && (
                    <div className="rounded-md border border-orange-300 bg-orange-50 p-2.5 text-xs text-orange-800 flex items-start gap-2" data-testid="gantt-alloc-override-warning">
                      <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold mb-0.5">Override required</div>
                        <div className="leading-snug">{allocOverrideWarning}</div>
                        <div className="mt-1 text-[10px] text-orange-700">Click <strong>Override</strong> to proceed — this allocation will be flagged as an Override.</div>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setAllocModal(null)}>Cancel</Button>
                    {allocOverrideWarning ? (
                      <Button size="sm" onClick={() => handleAddAllocation(true)} data-testid="gantt-alloc-override-btn" className="bg-orange-500 hover:bg-orange-600">
                        <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />Override
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => handleAddAllocation(false)} data-testid="gantt-alloc-confirm" className="bg-slate-900 hover:bg-slate-800">
                        <Plus className="w-3.5 h-3.5 mr-1.5" />Allocate
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Split allocation modal */}
          {splitModal && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={() => setSplitModal(null)}>
              <div className="bg-white rounded-lg shadow-xl p-6 w-96" onClick={(e) => e.stopPropagation()} data-testid="gantt-split-modal">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Split Allocation</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Split <span className="font-medium text-slate-700">{splitModal.equipmentItem?.internal_tag}</span> on <span className="font-medium text-slate-700">{splitModal.project?.name}</span> into two parts
                </p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">Split at date</Label>
                    <Input
                      type="date"
                      value={splitModal.splitDate}
                      onChange={(e) => setSplitModal({ ...splitModal, splitDate: e.target.value })}
                      data-testid="gantt-split-date-input"
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">First half ends day before, second half starts on this date</p>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setSplitModal(null)}>Cancel</Button>
                    <Button size="sm" onClick={handleSplitAllocation} data-testid="gantt-confirm-split-button" className="bg-slate-900 hover:bg-slate-800">
                      <Scissors className="w-3.5 h-3.5 mr-1.5" />Split
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
